import { invokeWithSingleContextHeal } from "@openmeld/adapter-core";
import { isLikelyContextLostError } from "../context/codex-context-heal.js";
import { CodexAppServerTransportError, invokeCodexAppServerTransport, } from "../transport/codex-app-server-transport.js";
import { invokeCodexCliTransport } from "../transport/codex-cli-transport.js";
export class CodexRuntimeError extends Error {
    agentControllerConversationOccupancy;
    code;
    errorType;
    originalError;
    providerAccessEvidence;
    constructor(input) {
        super(input.message);
        this.name = "CodexRuntimeError";
        this.agentControllerConversationOccupancy =
            input.agentControllerConversationOccupancy;
        this.code = input.code;
        this.errorType = input.errorType;
        this.originalError = input.originalError;
        this.providerAccessEvidence = input.providerAccessEvidence;
    }
}
export async function invokeCodexWithSingleContextHeal(input) {
    const result = await invokeWithSingleContextHeal({
        contextId: input.threadId,
        contextLossPolicy: input.computerFileAccessBoundary?.executionContextAccess ||
            input.executionMode === "repair"
            ? "fail"
            : "rebuild",
        createRebuildFailedError: (error) => new CodexRuntimeError({
            code: "context.rebuild_failed",
            errorType: "adapter",
            message: `context.rebuild_failed: ${toErrorMessage(error)}`,
        }),
        invokeOnce: (threadId) => invokeCodexOnce({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            codexTimeoutMs: input.codexTimeoutMs,
            codexWorkdir: input.codexWorkdir,
            conversationPolicy: input.conversationPolicy,
            computerFileAccessBoundary: input.computerFileAccessBoundary,
            env: input.env,
            executionMode: input.executionMode,
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent
                ? { onRuntimeEvent: input.onRuntimeEvent }
                : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            threadId,
        }).then((value) => ({
            contextId: value.threadId,
            output: value.output,
            ...(value.telemetry ? { telemetry: value.telemetry } : {}),
        })),
        isContextLostError: (error) => isLikelyContextLostError(error instanceof Error ? error.message : String(error)),
        onContextLost: input.onContextLost,
    });
    return {
        contextHealed: result.contextHealed,
        output: result.output,
        ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        threadId: result.contextId,
    };
}
export async function invokeCodexAppServer(input) {
    const result = await invokeWithSingleContextHeal({
        contextId: input.threadId,
        contextLossPolicy: input.computerFileAccessBoundary?.executionContextAccess ||
            input.executionMode === "repair"
            ? "fail"
            : "rebuild",
        createRebuildFailedError: (error) => new CodexRuntimeError({
            code: "context.rebuild_failed",
            errorType: "adapter",
            message: `context.rebuild_failed: ${toErrorMessage(error)}`,
        }),
        invokeOnce: (threadId) => invokeCodexAppServerOnceWithAdmissionRetry({
            ...input,
            threadId,
        }).then((value) => ({
            contextId: value.threadId,
            output: value.output,
            telemetry: value.telemetry,
        })),
        isContextLostError: isCodexAppServerContextLostError,
    });
    return {
        contextHealed: result.contextHealed,
        output: result.output,
        ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        threadId: result.contextId,
    };
}
async function invokeCodexAppServerOnceWithAdmissionRetry(input) {
    let firstStartupFailure = null;
    while (true) {
        try {
            return await invokeCodexAppServerTransport(input);
        }
        catch (error) {
            if (error instanceof CodexAppServerTransportError &&
                error.code === "runtime.startup_timeout") {
                if (!firstStartupFailure) {
                    firstStartupFailure = error;
                    continue;
                }
                throw new CodexRuntimeError({
                    code: error.code,
                    errorType: error.errorType,
                    message: `${error.message} after one automatic retry`,
                    originalError: firstStartupFailure.message,
                });
            }
            // A timed-out thread/start may have allocated a thread. Unlike the
            // initialize handshake, it has no retry-safe allocation contract.
            const failure = toCodexRuntimeError(error);
            if (firstStartupFailure) {
                throw new CodexRuntimeError({
                    ...failure,
                    message: failure.message,
                    originalError: firstStartupFailure.message,
                });
            }
            throw failure;
        }
    }
}
function isCodexAppServerContextLostError(error) {
    return isLikelyContextLostError(error instanceof Error ? error.message : String(error));
}
function toCodexRuntimeError(error) {
    if (error instanceof CodexRuntimeError) {
        return error;
    }
    if (error &&
        typeof error === "object" &&
        "code" in error &&
        typeof error.code === "string" &&
        "errorType" in error &&
        typeof error.errorType === "string" &&
        error instanceof Error) {
        return new CodexRuntimeError({
            agentControllerConversationOccupancy: error.agentControllerConversationOccupancy,
            code: error.code,
            errorType: error.errorType,
            message: error.message,
            originalError: error.originalError,
            providerAccessEvidence: error.providerAccessEvidence,
        });
    }
    return new CodexRuntimeError({
        code: "upstream_unreachable",
        errorType: "adapter",
        message: `upstream_unreachable: ${toErrorMessage(error)}`,
    });
}
async function invokeCodexOnce(input) {
    try {
        return await invokeCodexCliTransport({
            agentControllerPermissionMode: input.agentControllerPermissionMode,
            launchContract: input.launchContract,
            codexTimeoutMs: input.codexTimeoutMs,
            codexWorkdir: input.codexWorkdir,
            conversationPolicy: input.conversationPolicy,
            computerFileAccessBoundary: input.computerFileAccessBoundary,
            env: input.env,
            executionMode: input.executionMode,
            ...(input.onProcessControlEvent
                ? { onProcessControlEvent: input.onProcessControlEvent }
                : {}),
            ...(input.onPreview ? { onPreview: input.onPreview } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            streamingMode: input.streamingMode,
            threadId: input.threadId,
        });
    }
    catch (error) {
        if (error instanceof CodexRuntimeError) {
            throw error;
        }
        if (error &&
            typeof error === "object" &&
            "code" in error &&
            typeof error.code === "string" &&
            "errorType" in error &&
            typeof error.errorType === "string" &&
            error instanceof Error) {
            throw new CodexRuntimeError({
                code: error.code,
                errorType: error.errorType,
                message: error.message,
            });
        }
        throw new CodexRuntimeError({
            code: "upstream_unreachable",
            errorType: "adapter",
            message: `upstream_unreachable: ${toErrorMessage(error)}`,
        });
    }
}
function toErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    return String(error);
}
